import { Component } from '@theme/component';

/**
 * Custom element that mirrors the current slide of a `slideshow-component`
 * gallery elsewhere on the page onto a set of external pagination circles.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement[]} [circles]
 *
 * @extends {Component<Refs>}
 */
export class GalleryPagination extends Component {
  /** @type {(Element & { current?: number }) | null} */
  #gallery = null;

  connectedCallback() {
    super.connectedCallback();

    const galleryId = this.dataset.galleryId;
    this.#gallery = galleryId ? document.getElementById(galleryId) : null;

    if (!this.#gallery) return;

    this.#gallery.addEventListener('slideshow:select', this.#handleSelect);
    this.#updateSelected(this.#gallery.current ?? 0);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#gallery?.removeEventListener('slideshow:select', this.#handleSelect);
  }

  /** @param {CustomEvent<{ index: number }>} event */
  #handleSelect = (event) => {
    this.#updateSelected(event.detail.index);
  };

  /** @param {number} index */
  #updateSelected(index) {
    this.refs.circles?.forEach((circle, i) => {
      circle.setAttribute('aria-selected', `${i === index}`);
    });
  }
}

if (!customElements.get('gallery-pagination')) {
  customElements.define('gallery-pagination', GalleryPagination);
}
